import { cn } from "@/lib/utils";
import { Star } from "lucide-react";
import Image from "next/image";

export interface TestimonialAuthor {
  name: string;
  role: string;
  image: string;
}

export interface TestimonialCardProps {
  author: TestimonialAuthor;
  quote: string;
  rating?: number;
  className?: string;
}

export function TestimonialCard({
  author,
  quote,
  rating = 5,
  className,
}: TestimonialCardProps) {
  return (
    <div
      className={cn(
        "flex flex-col rounded-2xl",
        "bg-surface-solid border border-border-light",
        "p-6 text-start",
        "hover:border-brand-1/20",
        "max-w-[380px] min-w-[380px]",
        "transition-all duration-300",
        "group relative overflow-hidden",
        className,
      )}
    >
      {/* Subtle gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-1/[0.02] via-transparent to-brand-2/[0.02] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Rating stars */}
      <div className="flex gap-0.5 mb-4 relative z-10">
        {Array.from({ length: rating }).map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
        ))}
      </div>

      {/* Quote */}
      <p className="text-text-body text-sm sm:text-base leading-relaxed mb-6 relative z-10">
        &ldquo;{quote}&rdquo;
      </p>

      {/* Author */}
      <div className="flex items-center gap-3 mt-auto relative z-10">
        <div className="w-12 h-12 rounded-full overflow-hidden border border-border-light">
          <Image
            src={author.image}
            alt={author.name}
            width={48}
            height={48}
            className="object-cover w-full h-full"
          />
        </div>
        <div className="flex flex-col items-start">
          <h3 className="text-sm font-semibold leading-none text-text-primary">
            {author.name}
          </h3>
          <p className="text-sm text-text-secondary mt-1">{author.role}</p>
        </div>
      </div>
    </div>
  );
}
